import { useMemo } from 'react'
import { Link } from 'wouter'
import { useQuery } from '@apollo/client/react'
import { Trans, useLingui } from '@lingui/react/macro'
import { goodie as goodieHelpers } from '@guestgoodie/helpers'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'

import { RequestTimes } from './RequestTimes'

export function RequestsInfo({
  order
}: {
  order: GraphQL.OrderFragmentFragment
}) {
  const { t } = useLingui()

  const { data: goodieData, loading: goodieLoading } = useQuery(
    AdminAPI.goodie.GET,
    {
      variables: { id: order.goodieId }
    }
  )

  const { data: promotionData, loading: promotionLoading } = useQuery(
    AdminAPI.promotion.GET,
    {
      variables: { id: order.promotionId ?? -1 },
      skip: !order.promotionId
    }
  )

  const goodie = getFragment(
    SharedAPI.goodie.GOODIE_FRAGMENT,
    goodieData?.getGoodie
  )

  const promotion = getFragment(
    SharedAPI.promotion.PROMOTION_FRAGMENT,
    promotionData?.getPromotion
  )

  const link = useMemo(() => {
    if (promotion) return `/promotion/${promotion.token}`
    if (goodie) return `/goodie/${goodie.token}`
    return ''
  }, [goodie, promotion])

  const amount = useMemo(() => {
    switch (order.__typename) {
      case 'OrderEvent':
        return order.event.amount
      case 'OrderDefault':
        return order.default.amount
    }
  }, [order])

  const type = useMemo(() => {
    switch (order.__typename) {
      case 'OrderEvent':
        return t`Event`
      case 'OrderDefault':
        return t`Offer`
    }
  }, [order, t])

  const fieldData = useMemo(() => {
    const data = order.requestFieldData

    if (!data) return null

    return Object.entries(
      JSON.parse(order.requestFieldData) as Record<string, string>
    )
  }, [order.requestFieldData])

  if (promotionLoading || goodieLoading) {
    return (
      <div className="flex flex-col gap-1 opacity-30">
        <div className="h-5 w-40 animate-pulse rounded-small bg-text" />
        <div className="h-5 w-56 animate-pulse rounded-small bg-text-light" />
      </div>
    )
  }

  if (!goodie) {
    return (
      <div>
        <span>
          <Trans comment="Generic error when something unexpected happened">
            Something went wrong.
          </Trans>
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 className="text-heading3 text-text">
          <span className="font-black">{amount}x </span>
          <Link className="text-info-500" to={link}>
            {promotion?.title ?? goodie?.title}
          </Link>
        </h3>
        <span className="text-regular text-text-light">
          {goodieHelpers.toInfoFromGoodieFragment(goodie)}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-regular text-text">
          <Trans>Goodie Type</Trans>:
        </span>
        <span className="text-regular font-normal text-text-light">{type}</span>
      </div>
      {order.__typename === 'OrderEvent' && (
        <Link
          to={`/edit-event-occurence/${order.event.eventOccurence.token}`}
          className="text-regular text-info-500"
        >
          <Trans>Participants</Trans>
        </Link>
      )}
      <RequestTimes order={order} goodie={goodie} />
      {fieldData && (
        <div className="flex flex-col gap-1">
          <span className="text-regular text-text">
            <Trans>Data given by Guest</Trans>
          </span>
          <div className="flex flex-col gap-1">
            {fieldData?.map(([key, value]) => (
              <div className="flex flex-row gap-1 items-baseline" key={key}>
                <span className="text-text-light text-regular italic">
                  {key}
                  {': '}
                </span>
                <span className="text-text text-regular">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {order.guestMessage && (
        <div className="flex flex-col gap-1">
          <span className="text-regular text-text">
            <Trans>Message</Trans>:
          </span>
          <p className="text-regular text-text-light">{order.guestMessage}</p>
        </div>
      )}
    </div>
  )
}
